import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { WorkspaceViewArticleItem } from '@/@types/WorkspaceViewArticleItem';
import workspaceService from '@/services/workspace/WorkspaceService';
import store from '@/store';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';
import { ActionContext } from 'vuex';
import { State } from '@/store/state';
import confirmationService from '@/services/ConfirmationService';
import apiService from '@/services/ApiService';
import {
  dispatchCloseArticleVersionMode,
  dispatchResetArticleVersionView,
  dispatchSetArticleVersionToSidebar,
} from '@/store/dispatchers/articleVersionDispatchers';
import { Loading } from 'element-ui';
import authService from '@/services/AuthService';
import articleEditService from '@/services/article/ArticleEditService';
import { Article } from '@/models/article/Article';
import { commitSetSelectedArticle, commitSetWorkspaceViewArticles } from '@/store/commits/workspaceViewCommits';
import { ArticleVersion } from '@/@types/ArticleVersion';
import { commitSetArticleEditorArticle } from '@/store/commits/articleEditorCommits';
import moment from 'moment';
import { ArticleVersionView } from '@/@types/ArticleVersionView';
import { commitSetArticleVersionView } from '@/store/commits/articleVersionCommits';

export const articleVersionActions = {
  async deleteArticleVersion({ state }: ActionContext<State, any>) {
    const articleVersionTime = state.articleVersionView.time;

    const result = await confirmationService.showDeleteArticleVersionConfirmation(articleVersionTime).catch(() => null);
    if (!result) return;

    try {
      const selectedArticleVersionId = state.articleVersionView.articleId;
      const version = state.articleVersionView.version;
      await apiService.article.deleteVersion(state.workspaceView.workspace.id, selectedArticleVersionId, version);
      await dispatchCloseArticleVersionMode(state.workspaceView.selectedArticle.id);
    } catch (error) {
      apiService.handleResponseError(error);
    }
  },

  async restoreArticleVersion({ state }: ActionContext<State, {}>) {
    const result = await confirmationService
      .showRestorePopup(state.articleVersionView.time, 'restore-article-version')
      .catch(() => null);
    if (!result) return;

    const loader = Loading.service({
      lock: true,
      text: 'Restoring article version',
    });
    try {
      const { workspaceView, articleVersionView } = state;
      const selectedArticle = workspaceView.selectedArticle;
      const versionToRestore = articleVersionView.version;
      const workspaceId = workspaceView.workspace.id;
      const userId = authService.user.id;

      await articleEditService.save({
        updatedByUser: userId,
        isPublish: true,
        isRestore: true,
        isKeepDraft: true,
      });

      await apiService.article.deleteVersion(workspaceId, selectedArticle.id, versionToRestore);

      const restoredArticle = articleEditService.articleEditor.article;

      const updatedArticles: WorkspaceViewArticleItem[] = state.workspaceView.articles.map((a) => {
        if (a.id === restoredArticle.id) {
          return {
            id: restoredArticle.id,
            title: restoredArticle.title,
            shortId: restoredArticle.shortId,
            isArchived: restoredArticle.isArchived,
            isHasVideo: !!restoredArticle.paragraphs.length,
            isHasNotes: !!restoredArticle.textTabs.length,
            isShared: false,
          };
        }
        return a;
      });
      commitSetWorkspaceViewArticles(updatedArticles);
      commitSetSelectedArticle(restoredArticle);
      await dispatchResetArticleVersionView();
    } catch (err) {
      apiService.handleResponseError(err);
    } finally {
      loader.close();
    }
  },

  async loadArticleVersion({ state }: ActionContext<State, {}>, item: ArticleVersion) {
    const loader = Loading.service({
      lock: true,
      text: 'Loading Article Version',
    });
    try {
      const currentArticleMode = store.state.articleMode;
      const response = await apiService.article.getVersion(
        state.workspaceView.workspace.id,
        state.workspaceView.selectedArticle.id,
        item.version
      );
      const article = Article.fromJson(response.data.article);
      article.id = response.data.article.articleId;
      article.workspace = state.workspaceView.workspace.id;
      await dispatchSetArticleVersionToSidebar(article);
      commitSetSelectedArticle(article);
      commitSetArticleEditorArticle(article);

      const isParagraphs = !!article?.paragraphs?.length;
      const isTextTabs = !!article.textTabs.length;

      if (currentArticleMode === ArticleEditMode.VIDEO && !isParagraphs) {
        commitSetArticleMode(ArticleEditMode.NOTES);
      }

      if (currentArticleMode === ArticleEditMode.NOTES || (isTextTabs && !isParagraphs)) {
        workspaceService.setActiveTextTab({ article });
      }

      const { firstName, lastName } = item.author;
      const author = firstName && lastName ? `${firstName} ${lastName}` : `Unknown author`;
      const time = moment(item.time).calendar(null, {
        lastWeek: 'MMM DD hh:mm a',
        lastDay: 'MMM DD hh:mm a',
        sameDay: '[Today] hh:mm a ',
        sameElse: 'MMM DD hh:mm a',
      });
      const articleVersionView = {
        isShowFooter: true,
        articleId: state.workspaceView.selectedArticle.id,
        version: item.version,
        author: author,
        time,
        profileImage: item.profileImage,
      } as ArticleVersionView;
      commitSetArticleVersionView(articleVersionView);
    } catch (err) {
      apiService.handleResponseError(err);
    } finally {
      loader.close();
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async closeArticleVersionMode({ state }: ActionContext<State, {}>, articleId: string = null) {
    const loader = Loading.service({
      lock: true,
      text: 'Quitting the version mode',
    });
    try {
      await dispatchResetArticleVersionView();

      const articleRes = await apiService.article.get(articleId);
      const article = Article.fromJson(articleRes.data.article);
      if (article) {
        const updatedArticles = state.workspaceView.articles.map((a) => {
          if (a.id === article.id) {
            return {
              id: article.id,
              title: article.title,
              shortId: article.shortId,
              isArchived: article.isArchived,
              isHasVideo: !!article.paragraphs.length,
              isHasNotes: !!article.textTabs.length,
              isShared: false,
            };
          }
          return a;
        });
        commitSetWorkspaceViewArticles(updatedArticles);

        commitSetArticleEditorArticle(article);
        commitSetSelectedArticle(article);
      }
    } catch (err) {
      apiService.handleResponseError(err);
      loader.close();
    } finally {
      loader.close();
    }
  },

  resetArticleVersionView() {
    const articleVersionView = {
      author: null,
      isShowFooter: false,
      articleId: null,
      version: null,
      time: null,
      profileImage: null,
    } as ArticleVersionView;
    commitSetArticleVersionView(articleVersionView);
  },

  setArticleVersionIntoSidebar({ state }: ActionContext<State, {}>, articleVersion: Article) {
    const articles = state.workspaceView.articles;
    const updatedArticles = articles.map((article) => {
      if (article.id === articleVersion.id) {
        return {
          id: articleVersion.id,
          title: articleVersion.title,
          shortId: articleVersion.shortId,
          isArchived: articleVersion.isArchived,
          isHasVideo: !!articleVersion.paragraphs.length,
          isHasNotes: !!articleVersion.textTabs.length,
          isShared: false,
        };
      } else return article;
    });
    commitSetWorkspaceViewArticles(updatedArticles);
  },
};
